import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Button, Checkbox, HelperText, Label, Spinner, TextInput } from "flowbite-react";
import { HiCheck } from "react-icons/hi";
import { useAuthentication, useTranslation, useToast } from "@repo/core";
import SocialLoginButtons from "../components/auth/SocialLoginButtons";
import { isSocialLoginEnabled } from "../config/featureFlags";
import PasswordChecklist from "../components/auth/PasswordChecklist";
import { passwordMeetsAllRules } from "../utils/passwordRules";
import { extractErrorMessage, extractSignupFieldErrors } from "../utils/authErrors";
import type { SignupFieldErrors } from "../utils/authErrors";

const BENEFIT_KEYS = ["item1", "item2"] as const;

const Signup: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<SignupFieldErrors>({});
  // Registration succeeded but the backend requires e-mail verification
  // before a session can be established (mirrors anp-fe's Register.vue).
  const [succeeded, setSucceeded] = useState(false);

  const { register, isAuthenticated } = useAuthentication();
  const { addErrorMessage } = useToast();
  const { t, tCommon } = useTranslation();

  // If registration logged the user in directly, send them into the app.
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const passwordsMatch = password1 === password2;
  const canSubmit =
    email.trim().length > 0 &&
    passwordMeetsAllRules(password1) &&
    passwordsMatch &&
    termsAgreed;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setLoading(true);

    try {
      const { requiresVerification } = await register(email, password1, password2);
      if (requiresVerification) {
        setSucceeded(true);
      }
      // Otherwise isAuthenticated flips true above and this component
      // re-renders straight into the <Navigate> guard.
    } catch (error) {
      console.error("Registration failed:", error);
      const errors = extractSignupFieldErrors(error);
      if (Object.values(errors).some(Boolean)) {
        setFieldErrors(errors);
      } else {
        addErrorMessage(extractErrorMessage(error) ?? t("signup.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (succeeded) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow dark:bg-gray-800">
          <h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
            {t("signup.successTitle")}
          </h2>
          <p className="mb-2 text-gray-600 dark:text-gray-400">{t("signup.successBody1")}</p>
          <p className="font-medium text-gray-900 dark:text-white">{t("signup.successBody2")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-4">
      <div className="flex w-full max-w-5xl flex-col gap-8 lg:flex-row lg:items-center lg:justify-center">
        {/* Signup card - comes first in the DOM so it's also first when
            stacked on small screens; side-by-side with the benefits panel
            from lg upward. */}
        <div className="w-full rounded-lg bg-white p-8 shadow dark:bg-gray-800 lg:max-w-md">
          <h2 className="text-center text-2xl font-bold text-gray-900 dark:text-white">
            {t("signup.title")}
          </h2>
          <div className="mt-2 mb-6 flex justify-center">
            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
              {t("signup.freeTrialBadge")}
            </span>
          </div>

          {isSocialLoginEnabled && (
            <>
              <SocialLoginButtons />

              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                <span className="text-xs text-gray-400">{tCommon("orDivider")}</span>
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              </div>
            </>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="email">{t("signup.emailLabel")}</Label>
              <TextInput
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder={t("signup.emailPlaceholder")}
                value={email}
                color={fieldErrors.email ? "failure" : undefined}
                onChange={(e) => setEmail(e.target.value)}
              />
              {fieldErrors.email && <HelperText color="failure">{fieldErrors.email}</HelperText>}
            </div>

            <div>
              <Label htmlFor="password1">{t("signup.passwordLabel")}</Label>
              <TextInput
                id="password1"
                name="password1"
                type="password"
                autoComplete="new-password"
                required
                color={fieldErrors.password1 ? "failure" : undefined}
                value={password1}
                onChange={(e) => setPassword1(e.target.value)}
              />
              {fieldErrors.password1 && <HelperText color="failure">{fieldErrors.password1}</HelperText>}
              <PasswordChecklist password={password1} />
            </div>

            <div>
              <Label htmlFor="password2">{t("signup.password2Label")}</Label>
              <TextInput
                id="password2"
                name="password2"
                type="password"
                autoComplete="new-password"
                required
                color={fieldErrors.password2 || (password2.length > 0 && !passwordsMatch) ? "failure" : undefined}
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
              />
              {(fieldErrors.password2 || (password2.length > 0 && !passwordsMatch)) && (
                <HelperText color="failure">
                  {fieldErrors.password2 ?? t("signup.passwordMismatch")}
                </HelperText>
              )}
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                className="mt-0.5"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
              />
              <Label
                htmlFor="terms"
                className="text-xs font-normal text-gray-600 dark:text-gray-400"
              >
                {t("signup.termsPrefix")}{" "}
                <Link
                  to="/terms-and-conditions"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  {t("signup.termsLink")}
                </Link>{" "}
                {t("signup.andWord")}{" "}
                <Link
                  to="/privacy-policy"
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  {t("signup.privacyLink")}
                </Link>
                .
              </Label>
            </div>

            <Button type="submit" color="red" className="w-full" disabled={loading || !canSubmit}>
              {loading && <Spinner size="sm" className="mr-2" light />}
              {t("signup.submitButton")}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            {t("signup.alreadyHaveAccount")}{" "}
            <Link to="/login" className="font-medium text-blue-600 hover:underline dark:text-blue-400">
              {t("signup.signInLink")}
            </Link>
          </p>
        </div>

        {/* Benefits panel */}
        <div className="w-full lg:max-w-sm">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("signup.benefits.heading")}
          </h3>
          <ul className="space-y-4">
            {BENEFIT_KEYS.map((key) => (
              <li key={key} className="flex items-start gap-2">
                <HiCheck size={20} className="mt-0.5 shrink-0 text-green-500" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {t(`signup.benefits.${key}.title`)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t(`signup.benefits.${key}.description`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Signup;
